import Configuration from '@/agilis/util/Configuration'
import dialogFormMixin from '@/agilis/components/DialogForm/dialogFormMixin'
import JsonEditor from '@/agilis/components/JsonEditor/JsonEditor.vue'
import JsonEditorText from '@/agilis/components/JsonEditorText/JsonEditorText.vue'
import L from 'lodash'
import { mapActions, mapGetters } from 'vuex'

const d = require('deepdash').default
d(L)

export default {
  name: 'DocumentEditor',
  mixins: [dialogFormMixin],

  props: {
    document: Object,
    customComponents: Object
  },

  components: {
    JsonEditor,
    JsonEditorText
  },

  beforeDestroy () {
    clearInterval(this.autoSave)
  },

  data () {
    return {
      json: {},
      changedJson: null,
      currentVersion: '',
      autoSave: null,
      schema: null
    }
  },

  computed: {
    ...mapGetters({
      loading: 'loaderStore/loading',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      userId: 'userDataStore/userId',
      globalSchema: 'configurationStore/schema'
    }),

    documentTitle () {
      return this.document.name ? this.$t(this.document.name) : this.document.id
    },

    documentDescription () {
      return this.document.description && this.$t(this.document.description)
    },

    checkinDisabled () {
      return !this.json._checkoutFlag || (this.json._lastUpdatedUser !== this.userId && this.json._checkoutFlag)
    },

    saveDisabled () {
      return this.json._lastUpdatedUser !== this.userId || !this.json._checkoutFlag
    },

    checkoutDisabled () {
      return this.json._lastUpdatedUser !== this.userId && this.json._checkoutFlag
    },

    checkDocumentDisabled () {
      return !this.json._checkoutFlag || this.checkoutDisabled
    }
  },

  watch: {
    document: {
      handler (newValue) {
        if (newValue) {
          this.loadSchema()
          this.loadDocument()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyMessage': 'notificationStore/notifyMessage',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    loadSchema () {
      const baseDocument = { schema: 'documentSchema' }
      let baseSchema = Configuration.document.getDocumentSchema(this.applicationId, baseDocument)
      if (!baseSchema) {
        baseSchema = this.globalSchema[baseDocument.schema]
      }
      if (!baseSchema) {
        return null
      }
      baseSchema = L.cloneDeep(baseSchema)

      let contentSchema = Configuration.document.getDocumentSchema(this.applicationId, this.document)
      if (!contentSchema) {
        contentSchema = this.globalSchema[this.document.schema]
      }

      baseSchema.properties = Object.assign({}, baseSchema.properties, contentSchema?.properties)
      baseSchema.patternProperties = Object.assign({}, baseSchema.patternProperties, contentSchema?.patternProperties)

      let schema = { [baseDocument.schema]: baseSchema }
      schema.id = baseDocument.schema
      schema = Object.assign(schema, contentSchema?._defs)
      this.schema = L.cloneDeep(schema)
    },

    getAgilisUrlDocument () {
      let url
      switch (this.$route.name) {
        case 'applicationSettings':
          url = this.$axios.agilisUrlConfigurationPart(this.applicationId, this.document.id)
          break

        default:
          url = this.$axios.agilisUrlDocument(this.applicationId, this.document.id)
      }
      return url
    },

    async loadDocument () {
      const loader = { id: 'load-json-document' }

      try {
        this.startLoader(loader)

        const url = this.getAgilisUrlDocument(this.applicationId, this.document.id)
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        const response = (await this.$axios.get(url, config))
        this.json = response.data || this.createDocumentRoot()
        this.changedJson = null

        this.currentVersion = this.json.version

        if (this.json._checkoutFlag && this.json._lastUpdatedUser === this.userId) {
          this.autoSave = setInterval(() => {
            this.autoSaveTree()
          }, this.json._autoSaveMinutes ? this.json._autoSaveMinutes * 60000 : 300000)
        }
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    createDocumentRoot () {
      const newRoot = {
        id: this.document.id,
        name: this.document.name,
        version: '1.0.0',
        _lastUpdatedTime: null,
        _lastUpdatedUser: null,
        _autoSaveMinutes: 5,
        _checkoutFlag: false,
        _id: this.document.id
      }

      return newRoot
    },

    async saveJson (json) {
      this.changedJson = json
    },

    async autoSaveTree () {
      if (this.changedJson && this.changedJson._checkoutFlag && this.changedJson._lastUpdatedUser === this.userId) {
        const loader = { id: 'save-json-document' }

        setTimeout(async () => { // Why setTimeout? Make sure blur on typing input to complete the model updates
          // Update technical fields
          this.changedJson._lastUpdatedUser = this.userId
          this.changedJson._lastUpdatedTime = (new Date()).toISOString()

          try {
            this.startLoader(loader)

            this.currentVersion = this.changedJson.version

            // Api
            const url = this.getAgilisUrlDocument(this.applicationId, this.document.id).concat(`/version/${this.changedJson.version}`)
            const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)
            await this.$axios.post(url, this.changedJson, config)
          } catch (e) {
            this.notifyError(e)
          }

          this.stopLoader(loader) // TODO: decommentare quando togliamo setTimeout
        }, 0)
      }
    },

    async checkin () {
      const loader = { id: 'load-json-document' }

      if (!this.changedJson) {
        this.changedJson = L.cloneDeep(this.json)
      }

      this.changedJson._lastUpdatedUser = this.userId
      this.changedJson._lastUpdatedTime = (new Date()).toISOString()

      try {
        this.startLoader(loader)

        this.currentVersion = this.changedJson.version

        const url = this.getAgilisUrlDocument(this.applicationId, this.document.id).concat('/checkin')
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        await this.$axios.post(url, this.changedJson, config)

        clearInterval(this.autoSave)
        this.loadDocument()
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    async checkout () {
      const loader = { id: 'load-json-document' }

      try {
        this.startLoader(loader)

        const url = this.getAgilisUrlDocument(this.applicationId, this.document.id).concat('/checkout')
        const config = this.$axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT)

        const response = (await this.$axios.get(url, config))
        this.json = response.data
        this.changedJson = null

        this.autoSave = setInterval(() => {
          this.autoSaveTree()
        }, this.json._autoSaveMinutes ? this.json._autoSaveMinutes * 60000 : 300000)
      } catch (e) {
        this.notifyError(e)
      }
      this.stopLoader(loader)
    },

    userCanDoDocumentOperation (operation) {
      return Configuration.auth.userCanDoDocumentOperation(this.selectedContext, operation)
    },

    customValidator (columns) {
      if (columns.version) {
        columns.version = {
          ...columns.version,
          rules: {
            version_higher: this.currentVersion,
            regex: '^(\\d+\\.)?(\\d+\\.)?(\\*|\\d+)$'
          }
        }
      }
    },

    popupWarning (fieldConfig) {
      this.notifyMessage(this.$t(fieldConfig.message))
    }
  }
}
